import { z } from 'zod'
import { AbsChord, Label, Level, Observatory, Pc, ScaleId } from './music.ts'

/** Admitted licences: CC BY (3.0 and 4.0) and public domain (CC0, Public Domain Mark). SA, NC and ND never. */
export const Licence = z.enum(['CC BY 4.0', 'CC BY 3.0', 'CC0 1.0', 'Public Domain Mark 1.0'])
export type Licence = z.infer<typeof Licence>

export const LICENCE_URLS: Record<Licence, string> = {
  'CC BY 4.0': 'https://creativecommons.org/licenses/by/4.0/',
  'CC BY 3.0': 'https://creativecommons.org/licenses/by/3.0/',
  'CC0 1.0': 'https://creativecommons.org/publicdomain/zero/1.0/',
  'Public Domain Mark 1.0': 'https://creativecommons.org/publicdomain/mark/1.0/',
}

export const Chord = z.object({
  /** Index in the song's chord list. */
  i: z.number().int().min(0),
  /** Seconds of song audio. */
  start: z.number().min(0),
  end: z.number().min(0),
  /** Bar index (0-based) the chord starts in. */
  bar: z.number().int().min(0),
  /** Duration in beats. */
  beats: z.number().min(0),
  abs: AbsChord,
  label: Label,
  quiz: z.boolean(),
  /** Starts a new quiz span though the chord before it is a quiz chord: the curator's passage break (spec §7, §14). */
  passage: z.boolean().optional(),
  /** abs.bass == abs.root */
  rootPosition: z.boolean(),
  /** The detector wrote an extension the app reduces to its triad (6, 9, 11, 13, add); never a quiz chord (spec §6). */
  reduced: z.boolean().optional(),
})
export type Chord = z.infer<typeof Chord>

export const Section = z.object({
  start: z.number().min(0),
  end: z.number().min(0),
  label: z.string(),
})
export type Section = z.infer<typeof Section>

export const AudioFormat = z.enum(['aac', 'wav'])
export type AudioFormat = z.infer<typeof AudioFormat>

export const Attribution = z.object({
  source: z.string().min(1),
  sourceName: z.string().min(1),
  artistUrl: z.string().url().optional(),
  trackUrl: z.string().url(),
  licence: Licence,
  licenceUrl: z.string().url(),
  /** ISO date on which the curator read the licence on the track page. */
  verifiedAt: z.string().min(4),
  archiveUrl: z.string().url().optional(),
})
export type Attribution = z.infer<typeof Attribution>

export const SongId = z
  .string()
  .regex(/^[a-z0-9]+-[A-Za-z0-9._-]+$/, 'songId is <source>-<sourceId>')

/** Published song file, one per song and annotation version (spec §2). */
export const Song = z.object({
  id: SongId,
  annotationVersion: z.number().int().min(1),
  title: z.string().min(1),
  artist: z.string().min(1),
  genre: z.string(),
  bpm: z.number().positive(),
  duration: z.number().positive(),
  tonic: Pc,
  scale: ScaleId,
  /** The observatory that charts the song: its mode's parent (spec §1). */
  observatory: Observatory,
  level: Level,
  demo: z.boolean(),
  /** Median bass MIDI note of the quiz chords; voicing hint for the sampler (spec §15). */
  register: z.number().int().min(24).max(84),
  beat: z.object({
    barStarts: z.array(z.number().min(0)),
    beatsPerBar: z.number().int().min(1).max(12),
  }),
  sections: z.array(Section),
  chords: z.array(Chord),
  audio: z.object({
    url: z.string().min(1),
    sha256: z.string(),
    format: AudioFormat,
  }),
  attribution: Attribution,
})
export type Song = z.infer<typeof Song>

export const CatalogueNight = z.object({
  n: Level,
  songIds: z.array(SongId),
  demoIds: z.array(SongId),
  /** Charted songs that meet the night in this observatory (spec §8); 0 where it has none. */
  needed: z.number().int().min(0),
})
export type CatalogueNight = z.infer<typeof CatalogueNight>

export const CatalogueObservatory = z.object({
  observatory: Observatory,
  nights: z.array(CatalogueNight),
})
export type CatalogueObservatory = z.infer<typeof CatalogueObservatory>

export const CatalogueSongRef = z.object({
  url: z.string().min(1),
  /** For the credits (visual §6). */
  title: z.string(),
  artist: z.string(),
  annotationVersion: z.number().int().min(1),
  observatory: Observatory,
  level: Level,
  demo: z.boolean(),
})
export type CatalogueSongRef = z.infer<typeof CatalogueSongRef>

/**
 * The story scenes (visual §6), in the order a player meets them: the intro, one on the way into
 * each night from Nightfall to Before dawn, the sky charted (the last night met), the sunrise.
 */
export const STORIES = [
  'intro',
  'night2',
  'night3',
  'night4',
  'night5',
  'night6',
  'night7',
  'ending',
  'sunrise',
] as const
export const StoryName = z.enum(STORIES)
export type StoryName = z.infer<typeof StoryName>

/** The curator's choice of music beneath a story scene: a song, played from `from` seconds (spec §14). */
export const StoryMusic = z.object({
  songId: SongId,
  from: z.number().min(0).default(0),
})
export type StoryMusic = z.infer<typeof StoryMusic>

export const StoryMusicMap = z.partialRecord(StoryName, StoryMusic)
export type StoryMusicMap = z.infer<typeof StoryMusicMap>

/**
 * The music of a story scene as published: the song's audio and credit carried in the snapshot,
 * so that the scene plays without loading the song file.
 */
export const StoryTrack = StoryMusic.extend({
  title: z.string().min(1),
  artist: z.string().min(1),
  audio: Song.shape.audio,
  attribution: Attribution,
})
export type StoryTrack = z.infer<typeof StoryTrack>

/** One immutable snapshot (spec §2, §9). */
export const Catalogue = z.object({
  snapshot: z.number().int().min(1),
  publishedAt: z.string(),
  observatories: z.array(CatalogueObservatory),
  songs: z.record(SongId, CatalogueSongRef),
  /** The curator's music for the story scenes; a scene without it plays in silence. */
  story: z.partialRecord(StoryName, StoryTrack).optional(),
})
export type Catalogue = z.infer<typeof Catalogue>

/** The `current` pointer file. */
export const CurrentPointer = z.object({ snapshot: z.number().int().min(1) })
export type CurrentPointer = z.infer<typeof CurrentPointer>
